import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Box, Typography, Paper, Button, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Chip, Alert, CircularProgress,
  TextField, InputAdornment, IconButton, Tooltip, Divider,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  CheckCircle as OkIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

const InstitutionalEmails = () => {
  const [emails, setEmails]       = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [msg, setMsg]             = useState(null);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview]     = useState(null); // rows parsed from file
  const fileRef = useRef(null);

  const load = async (q = search) => {
    setLoading(true);
    try {
      const data = await adminAPI.getInstitutionalEmails({ search: q, limit: 200 });
      setEmails(data.emails || []);
      setTotal(data.total || 0);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Erreur de chargement' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });
        // Normalise column names (case-insensitive: CIN, Email, Password)
        const rows = raw.map(r => {
          const keys = Object.keys(r);
          const find = (pat) => keys.find(k => k.toLowerCase().includes(pat.toLowerCase()));
          return {
            cin:      String(r[find('cin')]      || '').trim(),
            email:    String(r[find('mail')]     || r[find('email')] || '').trim(),
            password: String(r[find('pass')]     || r[find('mot')]   || '').trim(),
          };
        }).filter(r => r.cin && r.email && r.password);
        setPreview(rows);
        setMsg({ type: 'info', text: `${rows.length} ligne(s) prête(s) à importer` });
      } catch (err) {
        setMsg({ type: 'error', text: 'Impossible de lire le fichier. Vérifiez le format.' });
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleImport = async () => {
    if (!preview?.length) return;
    setImporting(true);
    setMsg(null);
    try {
      const result = await adminAPI.importInstitutionalEmails(preview);
      setMsg({ type: 'success', text: `${result.processed} email(s) importé(s) avec succès.` });
      setPreview(null);
      load();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Erreur lors de l\'import.' });
    } finally { setImporting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet email ?')) return;
    try {
      await adminAPI.deleteInstitutionalEmail(id);
      setEmails(prev => prev.filter(e => e.id !== id));
      setTotal(t => t - 1);
    } catch (e) {
      setMsg({ type: 'error', text: 'Erreur lors de la suppression.' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon color="primary" /> Emails Institutionnels
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Gérez les adresses email institutionnelles attribuées aux étudiants.
        </Typography>
      </Box>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      {/* Import section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1.5px dashed #c7d2fe', bgcolor: '#f5f3ff' }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          Importer depuis Excel (.xlsx / .xls / .csv)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Le fichier doit contenir les colonnes : <strong>CIN</strong>, <strong>Email</strong>, <strong>Password</strong>
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFile} />
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => fileRef.current?.click()}
            sx={{ borderRadius: 2, borderColor: '#7c3aed', color: '#7c3aed' }}>
            Choisir un fichier
          </Button>
          {preview && (
            <>
              <Chip label={`${preview.length} lignes chargées`} color="primary" size="small" icon={<OkIcon />} />
              <Button variant="contained" startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                onClick={handleImport} disabled={importing}
                sx={{ borderRadius: 2, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
                {importing ? 'Import en cours…' : 'Importer'}
              </Button>
              <Button variant="text" size="small" onClick={() => setPreview(null)} color="inherit">Annuler</Button>
            </>
          )}
        </Box>

        {/* Preview table */}
        {preview && (
          <Box sx={{ mt: 2, maxHeight: 220, overflowY: 'auto', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#ede9fe' }}>
                  {['CIN', 'Email', 'Mot de passe'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.slice(0, 50).map((r, i) => (
                  <TableRow key={i} sx={{ '&:hover': { bgcolor: '#f5f3ff' } }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{r.cin}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{r.email}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#64748b' }}>{r.password}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {preview.length > 50 && (
              <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
                … et {preview.length - 50} autres lignes
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* Search + list */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef' }}>
        <Box sx={{ p: 2.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Rechercher par CIN ou email…" value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load()}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearch(''); load(''); }}><ClearIcon sx={{ fontSize: 16 }} /></IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260 }}
          />
          <Button variant="outlined" startIcon={<SearchIcon />} onClick={() => load()} sx={{ borderRadius: 2 }}>Chercher</Button>
          <Button variant="text" startIcon={<RefreshIcon />} onClick={() => load()} sx={{ borderRadius: 2 }}>Actualiser</Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {total} email(s)
          </Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>CIN</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Email institutionnel</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Mot de passe</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Importé le</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Par</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
              ) : emails.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.disabled' }}>Aucun email importé</TableCell></TableRow>
              ) : emails.map(e => (
                <TableRow key={e.id} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600 }}>{e.cin}</TableCell>
                  <TableCell sx={{ fontSize: '0.82rem', color: '#2563eb' }}>{e.email}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#64748b' }}>{e.password}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                    {new Date(e.imported_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{e.imported_by || '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Supprimer">
                      <IconButton size="small" color="error" onClick={() => handleDelete(e.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default InstitutionalEmails;
